import { Text } from "@certn/cascadia";
import {
  ExpandableCard,
  GhostIconButton,
  HoverInfo,
} from "@certn/cascadia-react";
import { formatDate } from "@certn/utils";
import { useTranslation } from "react-i18next";

import {
  CheckTypeStatus,
  CredentialRequestTypeBadge,
} from "./CredentialRequestsTypeBadge";
import { CredentialsJsonForm } from "./CredentialsJsonForm";
import { Schemas } from "../../types";

interface CredentialCardProps {
  name: string;
  isLocked: boolean;
  credentialDefinition: Schemas["CredentialDefinition"];
  credential: Schemas["CredentialJson"];
  status?: CheckTypeStatus;
  createdDate?: string;
  withdrawnDate?: string;
  sharedDate?: string;
  isWithdrawn?: boolean;
  onUnlock?: () => void;
}

export function CredentialCard(props: CredentialCardProps) {
  const { t } = useTranslation();

  const { name, isLocked, credentialDefinition, credential, status } = props;

  if (status === "WITHDRAWN") {
    return (
      <ExpandableCard
        title={name}
        subtitle={
          <>
            <CredentialRequestTypeBadge status={status} />
            <Text style="form/body-secondary" color="text-weak">
              {t("Withdrawn on {{date}}", {
                date: formatDate(props.sharedDate, "PP"),
              })}
            </Text>
          </>
        }
      />
    );
  }

  if (isLocked) {
    return (
      <ExpandableCard
        title={name}
        subtitle={`${t("Locked Credential")} • ${t("Shared {{date}}", {
          date: formatDate(props.sharedDate, "PP"),
        })}`}
        rightContent={
          <HoverInfo
            trigger={<GhostIconButton icon="lock" onPress={props.onUnlock} />}
            infoContent={t(
              "Credentials are locked by default and can only be unlocked upon purchase."
            )}
            testID="locked-credentials-info"
          />
        }
      />
    );
  }

  if (status === "CREDENTIALS_AVAILABLE" || status === "WITHDRAWAL_REQUESTED") {
    return (
      <ExpandableCard
        title={name}
        subtitle={
          <>
            <CredentialRequestTypeBadge status={status} />
            <Text style="form/body-secondary" color="text-weak">
              {t("Shared {{date}}", {
                date: formatDate(props.sharedDate, "PP"),
              })}
            </Text>
          </>
        }
      >
        <CredentialsJsonForm
          credentialDefinition={credentialDefinition}
          credential={credential}
        />
      </ExpandableCard>
    );
  }

  return (
    <ExpandableCard
      title={name}
      subtitle={t("Requested on {{date}}", {
        date: formatDate(props.createdDate, "PP"),
      })}
    />
  );
}
